import { Component, Input, OnInit } from '@angular/core';
import { BreadcrumbService, ProductNameService, AuthService } from 'src/app/services';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IRendererDetails, SituAnalyticsWebApiClient, GetRendererRequest, IRendererQueue,
  UpdateRendererQueueRequest, UpdateRendererQueuePrioritiesRequest, ProductName,
  IRecipeEventSummary, EnvironmentName, DeleteRendererRequest, RendererStatus } from 'situanalytics-webapi-client';
import { FormControl } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { RendererEditDialogComponent } from '../renderer-edit-dialog/renderer-edit-dialog.component';
import { AddQueueDialogComponent } from '../add-queue-dialog/add-queue-dialog.component';
import { ConfirmDialogService } from 'src/app/services';
import { Role } from 'src/app/shared/models/role.model';
import { isVirtualRenderer } from 'src/app/shared/models/renderer-type';
import { trigger, state, style, animate, transition } from '@angular/animations';

@Component({
  selector: 'renderer-dashboard',
  templateUrl: './renderer-dashboard.component.html',
  styleUrls: ['./renderer-dashboard.component.scss'],
  animations: [
    trigger('cardAnimator', [
      transition('* => wiggle', [
        animate('300ms', style({ transform: 'translateX(10px)' })),
        animate('300ms', style({ transform: 'translateX(-10px)' })),
        animate('300ms', style({ transform: 'translateX(10px)' })),
        animate('300ms', style({ transform: 'translateX(0)' }))
      ]),
    ])
  ]
})

export class RendererDashboardComponent implements OnInit {
  @Input() rendererId: string = '';

  id: string = '';
  company: string = '';
  renderer: IRendererDetails | null = null;
  admin = false;
  readonly isVirtual = isVirtualRenderer;

  //rendererQueues
  loadingQueues: boolean = false;
  filteredQueues: IRendererQueue[] | undefined;
  searchField = new FormControl();
  expandedQueuesList: boolean = false;

  productName = ProductName
  environmentName = EnvironmentName;
  filteredRecipes: IRecipeEventSummary[] | undefined;

  expandedRecipesList: boolean = false;
  animateAddQueue = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private analyticsApi: SituAnalyticsWebApiClient,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private productNameService: ProductNameService,
    private dialog: MatDialog,
    private confirmDialog: ConfirmDialogService,
    private authService: AuthService) { 
    this.admin = (this.authService.hasRole(Role.SituSystemsDeveloper) || this.authService.hasRole(Role.SituSystemsAdmin));
  }

  ngOnInit(): void {
    this.id = this.route.snapshot.params['rendererId'];
    this.loadRenderer();
  }

  private loadRenderer(): void {
    let request = new GetRendererRequest();
    request.id = this.id;
    this.analyticsApi.getRenderer(request)
      .then(response => {
        if (response.success) {
          this.renderer = response.renderer ?? null;
          console.log('Renderer loaded:', this.renderer);
          console.log('Renderer status:', this.renderer?.rendererStatus);
          this.breadcrumbService.setParam('rendererName', this.renderer?.name);
          this.doFilterQueues(this.searchField.value);
          this.doFilterRecipes();
        } else {
          this.snackBar.open(`Could not fetch the renderer.`, 'OK', { duration: 2000 });
        }
      });
  }

  // queues
  doFilterQueues = (value: string | undefined | null = undefined) => {
    if (!this.isStringNullOrEmpty(value)) {
      let searchTerm = value!.trim().toLocaleLowerCase();
      this.filteredQueues = this.renderer?.rendererQueues
        ?.filter(e => { return e.queueName?.toLocaleLowerCase().includes(searchTerm) })
        ?.sort(this.sortRendererQueues);
    } else {
      this.filteredQueues = this.renderer?.rendererQueues?.sort(this.sortRendererQueues);
    }
  }

  sortRendererQueues(a: IRendererQueue, b: IRendererQueue) {
    // Check if a or b is undefined
    if (!a || !b) {
      return 0;
    }

    // Sort by priority
    if ((a.priority ?? 0) < (b.priority ?? 0)) {
      return -1;
    }
    if ((a.priority ?? 0) > (b.priority ?? 0)) {
      return 1;
    }

    // If priorities are equal, sort by name
    if ((a.queueName ?? '') < (b.queueName ?? '')) {
      return -1;
    }
    if ((a.queueName ?? '') > (b.queueName ?? '')) {
      return 1;
    }

    return 0;
  }

  navigateToQueue(queueId: string) {

  }

  toggleQueueEnabled(rendererQueue: IRendererQueue) {
    this.loadingQueues = true;

    let request = new UpdateRendererQueueRequest();
    request.id = rendererQueue.id;
    request.enabled = !rendererQueue.enabled;
    request.priority = rendererQueue.priority;

    this.analyticsApi.updateRendererQueue(request).then(response => {
      if (!response.success) {
        this.snackBar.open(`Could not toggle the queue.`, 'OK', { duration: 2000 });
      }
      else {
        rendererQueue.enabled = !rendererQueue.enabled;
      }
    }).finally(() => {
      this.loadingQueues = false;
    });
  }

  toggleExpandedQueuesList() {
    this.expandedQueuesList = !this.expandedQueuesList;
  }

  drop(event: CdkDragDrop<IRendererQueue[]>) {
    this.moveSelected(event.currentIndex, event.previousIndex);
    this.savePriorities(this.getOrdersFromDisplayedItems());
  }

  private moveSelected(currentIndex: number, previousIndex: number | undefined = undefined) {
    if (previousIndex === undefined) {
      throw new Error("Previous index undefined")
    }
    moveItemInArray(this.renderer!.rendererQueues!, previousIndex ?? 0, currentIndex);

    this.reorderDataSource();
  }

  private reorderDataSource() {
    for (let i = 0; i < this.renderer!.rendererQueues!.length; ++i) {
      this.renderer!.rendererQueues![i].priority = 1 + i;
    }
  }

  private getOrdersFromDisplayedItems(): { [key: string]: number; } {
    let orders: { [key: string]: number; } = {};
    this.renderer!.rendererQueues!.forEach((rendererQueue: IRendererQueue) => {
      orders[rendererQueue?.id ?? ""] = rendererQueue?.priority ?? 0;
    });
    return orders;
  }

  private savePriorities(priorityConfigOrders: { [key: string]: number; }) {
    this.loadingQueues = true;

    let request = new UpdateRendererQueuePrioritiesRequest();
    request.rendererQueueIds = priorityConfigOrders;

    this.analyticsApi.updateRendererQueuePriorities(request)
      .then((response) => {
        if (!response.success || response.errorMessages?.length) {
          this.snackBar.open(`Error: ${response.errorMessages}!`, 'OK', { duration: 2000 });
        }
      }).catch((response) => {
        this.snackBar.open(`Error: ${response.errorMessages}!`, 'OK', { duration: 2000 });
      }).finally(() => {
        this.loadingQueues = false;
      });
  }

  // Recipes
  doFilterRecipes = (value: string | undefined | null = undefined) => {
    if (!this.isStringNullOrEmpty(value)) {
      let searchTerm = value!.trim().toLocaleLowerCase();
      this.filteredRecipes = this.renderer?.recipeEventSummaries
        ?.filter(e => { return e.queueName?.toLocaleLowerCase().includes(searchTerm) })
        ?.sort(this.sortRecipes);
    } else {
      this.filteredRecipes = this.renderer?.recipeEventSummaries?.sort(this.sortRecipes);
    }
  }

  sortRecipes(a: IRecipeEventSummary, b: IRecipeEventSummary) {
    // Check if both items are in progress
    const aIsInProgress = !a.dateCancelled && !a.dateFailed && !a.dateCompleted && !!a.dateStarted;
    const bIsInProgress = !b.dateCancelled && !b.dateFailed && !b.dateCompleted && !!b.dateStarted;

    if (aIsInProgress && bIsInProgress) {
      // Compare by dateStarted
      return b.dateStarted!.getTime() - a.dateStarted!.getTime();
    } else if (aIsInProgress) {
      // Sort in-progress items first
      return -1;
    } else if (bIsInProgress) {
      // Sort in-progress items first
      return 1;
    } else {
      // Sort by dateCompleted or dateCancelled or dateFailed descending
      const aDate = a.dateCompleted || a.dateCancelled || a.dateFailed || new Date(0);
      const bDate = b.dateCompleted || b.dateCancelled || b.dateFailed || new Date(0);
      return bDate.getTime() - aDate.getTime();
    }
  }

  navigateToRecipes(recipeEventSummaryId: string) {

  }

  toggleExpandedRecipesList() {
    this.expandedRecipesList = !this.expandedRecipesList;
  }

  getStatusIconAndClass(recipeEventSummary: IRecipeEventSummary) {
    // in progress
    const isInProgress = !recipeEventSummary.dateCancelled && !recipeEventSummary.dateFailed
      && !recipeEventSummary.dateCompleted && !!recipeEventSummary.dateStarted;
    if (isInProgress) {
      return { icon: "pending", class: "text-info", description: "In Progress" };
    }

    // completed
    if (!!recipeEventSummary.dateCompleted) {
      return { icon: "check_circle", class: "text-success", description: "Completed" };
    }

    // failed
    if (!!recipeEventSummary.dateFailed) {
      return { icon: "error", class: "text-danger", description: "Failed" };
    }

    // cancelled
    if (!!recipeEventSummary.dateCancelled) {
      return { icon: "block", class: "text-muted", description: "Cancelled" };
    }

    // queued
    if (!!recipeEventSummary.dateQueued) {
      return { icon: "pending", class: "text-muted", description: "Queued" };
    }
  }

  getProductColour(productName: ProductName) {
    return this.productNameService.getProductNameConfiguration(productName as any)?.productColor;
  }

  private isStringNullOrEmpty(str: string | undefined | null) {
    return str === null || str === undefined || str.trim().length === 0;
  }

  editRenderer(): void {
    if (!this.renderer) return;

    const dialogRef = this.dialog.open(RendererEditDialogComponent, {
      width: '600px',
      data: { 
        renderer: this.renderer,
        isNew: false 
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadRenderer();
      }
    });
  }

  deleteRenderer(): void {
    if (!this.renderer) return;

    this.confirmDialog.open({
      title: 'Delete Renderer',
      message: `Are you sure you want to delete the renderer "${this.renderer.name}"?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      actionIcon: 'delete',
      actionColour: 'warn'
    });

    this.confirmDialog.confirmed().subscribe(result => {
      if (result) {
        this.performDelete();
      }
    });
  }

  private performDelete(): void {
    if (!this.renderer) return;

    const request = new DeleteRendererRequest();
    request.id = this.renderer.id;

    this.analyticsApi.deleteRenderer(request)
      .then(response => {
        if (response.success) {
          this.snackBar.open('Renderer deleted successfully', 'OK', { duration: 3000 });
          this.router.navigate(['/admin/renderers']);
        } else {
          this.snackBar.open('Failed to delete renderer', 'OK', { duration: 3000 });
        }
      })
      .catch(error => {
        console.error('Error deleting renderer:', error);
        this.snackBar.open('Error deleting renderer', 'OK', { duration: 3000 });
      });
  }

  onClickAddQueue(): void {
    // TODO: Implement add queue functionality for renderer
    // This would need to associate a queue with this specific renderer
    this.animateAddQueue = 'wiggle';
  }

  resetAddQueueAnimationState(): void {
    this.animateAddQueue = '';
  }

  getRendererStatusColour(rendererStatus: RendererStatus | undefined): string {
    if (rendererStatus === undefined || rendererStatus === null) return '';
    
    const status = rendererStatus as RendererStatus;
    switch (status) {
      case RendererStatus.Idle:
        return 'success';
      case RendererStatus.Busy:
        return 'danger';
      case RendererStatus.Down:
        return 'danger';
      case RendererStatus.Updating:
        return 'info';
      default:
        return '';
    }
  }

  getRendererStatus(rendererStatus: RendererStatus | undefined): string {
    if (rendererStatus === undefined || rendererStatus === null) return 'Unknown';
    
    const status = rendererStatus as RendererStatus;
    switch (status) {
      case RendererStatus.Idle:
        return 'Idle';
      case RendererStatus.Busy:
        return 'Busy';
      case RendererStatus.Down:
        return 'Down';
      case RendererStatus.Updating:
        return 'Updating';
      default:
        return '';
    }
  }
}
